import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CallApiService } from './services/call-api.service';
import { CallResponse, CallStatus, CallDirection, CallPageResponse } from './models/call.model';
import { ToastService } from '../../shared/services/toast.service';
import { ModalComponent } from '../../shared/components/ui/modal/modal.component';
import { CALL_DIRECTION_LABELS, CALL_STATUS_LABELS, labelFrom } from './call-labels';

type DirectionFilter = 'all' | 'INBOUND' | 'OUTBOUND' | 'MISSED';

@Component({
  selector: 'app-calls',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './call.component.html'
})
export class CallComponent implements OnInit {

  loading = false;
  error = '';

  calls: CallResponse[] = [];
  filtered: CallResponse[] = [];

  page = 0;
  size = 20;
  totalElements = 0;
  totalPages = 0;
  hasNext = false;
  hasPrevious = false;

  filterDirection: DirectionFilter = 'all';
  filterStatus: '' | CallStatus = '';
  searchQuery = '';
  showFilters = false;

  filtroDuracionMin = '';
  filtroDuracionMax = '';

  readonly statusOptions: { value: CallStatus; label: string }[] =
    (Object.keys(CALL_STATUS_LABELS) as CallStatus[]).map(value => ({
      value,
      label: CALL_STATUS_LABELS[value]
    }));

  constructor(
    private readonly callApi: CallApiService,
    private readonly toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  /**
   * Nombre del agente tal y como lo resuelve el backend (`agentName`, incluye
   * agentes desactivados). Nunca se pinta el id: ése va sólo en el tooltip.
   */
  agentLabel(call: CallResponse | null | undefined): string {
    if (!call?.agentId) return '—';
    return call.agentName?.trim() || 'Agente sin nombre disponible';
  }

  agentTitle(agentId: number | null | undefined): string {
    return agentId ? `Identificador interno: ${agentId}` : '';
  }

  /** Nombre del contacto resuelto por el backend (`contactName`). */
  contactLabel(call: CallResponse | null | undefined): string {
    if (!call?.contactId) return '—';
    return call.contactName?.trim() || 'Contacto sin nombre disponible';
  }

  contactTitle(contactId: number | null | undefined): string {
    return contactId ? `Identificador interno: ${contactId}` : '';
  }

  directionLabel(d: CallDirection | undefined): string {
    return labelFrom(CALL_DIRECTION_LABELS, d);
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.callApi.listCalls(this.page, this.size).subscribe({
      next: (page: CallPageResponse) => {
        this.calls = page.content || [];
        this.totalElements = page.totalElements ?? 0;
        this.totalPages = page.totalPages ?? 0;
        this.hasNext = page.hasNext ?? false;
        this.hasPrevious = page.hasPrevious ?? false;
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar las llamadas';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    let data = [...this.calls];

    if (this.filterDirection === 'INBOUND' || this.filterDirection === 'OUTBOUND') {
      data = data.filter(c => c.direction === this.filterDirection);
    } else if (this.filterDirection === 'MISSED') {
      data = data.filter(c => c.direction === 'INBOUND' && (c.status === 'NO_ANSWER' || c.status === 'BUSY'));
    }

    if (this.filterStatus) {
      data = data.filter(c => c.status === this.filterStatus);
    }

    if (this.searchQuery.trim()) {
      const term = this.searchQuery.trim().toLowerCase();
      data = data.filter(c =>
        (c.fromNumber || '').toLowerCase().includes(term) ||
        (c.toNumber || '').toLowerCase().includes(term) ||
        (c.callSid || '').toLowerCase().includes(term)
      );
    }

    if (this.filtroDuracionMin) {
      const min = parseInt(this.filtroDuracionMin, 10);
      if (!isNaN(min)) data = data.filter(c => (c.durationSeconds ?? 0) >= min);
    }
    if (this.filtroDuracionMax) {
      const max = parseInt(this.filtroDuracionMax, 10);
      if (!isNaN(max)) data = data.filter(c => (c.durationSeconds ?? 0) <= max);
    }

    this.filtered = data;
  }

  setFilter(dir: DirectionFilter): void {
    this.filterDirection = dir;
    this.aplicarFiltros();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  limpiarFiltros(): void {
    this.filterDirection = 'all';
    this.filterStatus = '';
    this.searchQuery = '';
    this.filtroDuracionMin = '';
    this.filtroDuracionMax = '';
    this.aplicarFiltros();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value;
    this.aplicarFiltros();
  }

  prevPage(): void {
    if (this.page > 0) {
      this.page -= 1;
      this.cargar();
    }
  }

  nextPage(): void {
    if (this.hasNext) {
      this.page += 1;
      this.cargar();
    }
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.totalPages) return;
    this.page = p;
    this.cargar();
  }

  get totalCalls(): number { return this.totalElements; }
  get filteredCount(): number { return this.filtered.length; }
  get activeCalls(): number {
    return this.calls.filter(c => c.status === 'IN_PROGRESS').length;
  }

  formatDuration(seconds: number | null | undefined): string {
    if (seconds == null) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  }

  formatDateTime(iso: string | undefined): { date: string; time: string } {
    if (!iso) return { date: '—', time: '' };
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
    };
  }

  isMissed(c: CallResponse): boolean {
    return c.direction === 'INBOUND' && (c.status === 'NO_ANSWER' || c.status === 'BUSY');
  }

  statusLabel(s: CallStatus | undefined): string {
    return labelFrom(CALL_STATUS_LABELS, s);
  }

  selectedCall: CallResponse | null = null;
  showDetailModal = false;

  viewCallDetail(c: CallResponse): void {
    this.selectedCall = c;
    this.showDetailModal = true;
  }

  closeDetailModal(): void {
    this.showDetailModal = false;
    this.selectedCall = null;
  }
}
